import { useMutation } from "relay-hooks";
import { generateChatIdeasMutation } from "../queries/chatIdeasMutation";
import { chatIdeasMutation as ChatIdeasMutation } from "../queries/__generated__/chatIdeasMutation.graphql";
import { AssistantMode } from "components/assistant/__generated__/AssistantMutation.graphql";
import { useCallback, useRef, useState } from "react";
import { useRemainingAiMessages } from "components/assistant/assistantHooks";
import { useGetState, useSetState } from "components/ReduxProvider";
import { useViewerQuery } from "queries/viewerQuery";
import { DifficultyEnum } from "sharedJs__generated/constants";
import { shuffle } from "lodash";

type ChatIdeasInput = {
  chatId?: string;
  question?: string;
  mode: AssistantMode;
  withTranslation?: boolean;
};

export const ideasConfig: Partial<Record<AssistantMode, { label: string; optionsLabel: string }>> = {
  assistant: { label: "ideas", optionsLabel: "ideas" },
  letsPractise: { label: "select answer", optionsLabel: "answer choices" },
  speaking: { label: "you can say", optionsLabel: "ideas" }
};

export type ChatIdeasList = ChatIdeasMutation["response"]["generateChatIdeas"];

export const useChatIdeas = (deckId: string) => {
  const [ideas, setIdeas] = useState<ChatIdeasList>([]);
  const remainingAiMessages = useRemainingAiMessages();
  const [mutate, { loading }] = useMutation<ChatIdeasMutation>(generateChatIdeasMutation);
  const latestQuestionRef = useRef<string | undefined>(undefined);

  const generateIdeas = useCallback(
    ({ chatId, question, mode, withTranslation }: ChatIdeasInput) => {
      setIdeas([]);
      if (!deckId || !mode) return;
      if (!ideasConfig[mode]) return;
      if (remainingAiMessages === 0) return;
      latestQuestionRef.current = question;

      mutate({
        variables: {
          chatId: chatId,
          question: question,
          deckId,
          mode,
          withTranslation
        },
        onCompleted: (response) => {
          if (latestQuestionRef.current !== question) return;
          setIdeas(shuffle(response.generateChatIdeas || []));
        },
        onError: (error) => {
          setIdeas([]);
        }
      });
    },
    [deckId, mutate, remainingAiMessages]
  );

  return {
    ideas,
    loading,
    generateIdeas
  };
};

export const useChatIdeasSettings = ({ mode }: { mode?: AssistantMode }) => {
  const settings = useGetState("chatIdeasSettings");
  const setSettings = useSetState("chatIdeasSettings");
  const { getFlag } = useViewerQuery();

  const isBeginnerOrEasy = [DifficultyEnum.beginner, DifficultyEnum.easy].includes(
    getFlag("myDifficulty")?.value as DifficultyEnum
  );

  const active = mode === "letsPractise" ? true : settings?.active ?? true; // default on
  const withTranslation = settings?.withTranslation ?? isBeginnerOrEasy; // default based on difficulty

  return [{ active, withTranslation }, setSettings] as const;
};
